import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { SplashScreen } from '@capacitor/splash-screen'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { type ReactNode } from 'react'
import { AuthProvider } from '@/contexts/AuthContext'
import { OnboardingProvider, useOnboarding } from '@/contexts/OnboardingContext'
import { useAuth } from '@/hooks/useAuth'
import Layout from '@/components/Layout'
import { supabase } from '@/lib/supabase'
import { PushNotifications } from '@capacitor/push-notifications'
import { usePushTokenRegistration } from '@/hooks/usePushTokenRegistration'
import { useIntroSeen } from '@/hooks/useIntroSeen'
import IntroSlider from '@/components/IntroSlider'
import { useChangelog } from '@/hooks/useChangelog'
import { ChangelogModal } from '@/components/ChangelogModal'
import { changelog } from '@/lib/changelog'
import { scheduleRecurringPrayerNotifications } from '@/hooks/useRecurringPrayerNotifications'
import { ensureDailyNotifAtStartup, ensureCheckInNotifAtStartup } from '@/hooks/useNotifications'
import { cleanOldDrafts } from '@/hooks/useAutoSave'

// ── Lazy-loaded pages + composants lourds ────────────────────────────────────
// OnboardingFlow importe firebase (FCM) → lazy pour l'exclure du bundle initial
const OnboardingFlow = lazy(() => import('@/components/OnboardingFlow'))
const LandingPage      = lazy(() => import('@/pages/LandingPage'))
const AuthPage         = lazy(() => import('@/pages/AuthPage'))
const Home             = lazy(() => import('@/pages/Home'))
const Journal          = lazy(() => import('@/pages/Journal'))
const PrayerPage       = lazy(() => import('@/pages/PrayerPage'))
const NewPrayerPage    = lazy(() => import('@/pages/NewPrayerPage'))
const EditPrayerPage   = lazy(() => import('@/pages/EditPrayerPage'))
const PrayerDetailPage = lazy(() => import('@/pages/PrayerDetailPage'))
const GoalsPage        = lazy(() => import('@/pages/GoalsPage'))
const NewGoalPage      = lazy(() => import('@/pages/NewGoalPage'))
const EditGoalPage     = lazy(() => import('@/pages/EditGoalPage'))
const GoalDetailPage   = lazy(() => import('@/pages/GoalDetailPage'))
const CheckinPage      = lazy(() => import('@/pages/CheckinPage'))
const VerseLibraryPage = lazy(() => import('@/pages/VerseLibraryPage'))
const NewVersePage     = lazy(() => import('@/pages/NewVersePage'))
const EditVersePage    = lazy(() => import('@/pages/NewVersePage'))
const VerseSearchPage        = lazy(() => import('@/pages/VerseSearchPage'))
const SermonNoteDetailPage   = lazy(() => import('@/pages/SermonNoteDetailPage'))
const DevotionalDetailPage   = lazy(() => import('@/pages/DevotionalDetailPage'))
const VerseThemesPage  = lazy(() => import('@/pages/VerseThemesPage'))
const NotesPage        = lazy(() => import('@/pages/NotesPage'))
const DailyVersePage   = lazy(() => import('@/pages/DailyVersePage'))
const Profile          = lazy(() => import('@/pages/Profile'))
const FriendsPage      = lazy(() => import('@/pages/FriendsPage'))
const NotificationsPage       = lazy(() => import('@/pages/NotificationsPage'))
const NotificationSettingsPage = lazy(() => import('@/pages/NotificationSettingsPage'))
const PrivacyPolicyPage    = lazy(() => import('@/pages/PrivacyPolicyPage'))
const TermsPage            = lazy(() => import('@/pages/TermsPage'))
const ResetPasswordPage    = lazy(() => import('@/pages/ResetPasswordPage'))
const DownloadPage         = lazy(() => import('@/pages/DownloadPage'))
const InvitePage           = lazy(() => import('@/pages/InvitePage'))
const InviteAutoPage       = lazy(() => import('@/pages/InviteAutoPage'))
const GuidePage            = lazy(() => import('@/pages/GuidePage'))
const StoryTestPage        = lazy(() => import('@/pages/StoryTestPage'))
const GroupPage            = lazy(() => import('@/pages/GroupPage'))
const CreateGroupPage      = lazy(() => import('@/pages/CreateGroupPage'))
const JoinGroupPage        = lazy(() => import('@/pages/JoinGroupPage'))
const GroupAnsweredPage    = lazy(() => import('@/pages/GroupAnsweredPage'))
const GroupInvitePage      = lazy(() => import('@/pages/GroupInvitePage'))

// ── Spinner ──────────────────────────────────────────────────────────────────
function GlobalSpinner() {
  return (
    <div
      className="flex items-center justify-center"
      style={{ height: '100dvh', backgroundColor: 'var(--selah-bg)' }}
    >
      <div
        className="w-10 h-10 rounded-full border-2 animate-spin"
        style={{ borderColor: 'var(--selah-accent)', borderTopColor: 'transparent' }}
      />
    </div>
  )
}

// ── Onboarding gate — shown once, before the app shell ───────────────────────
// Lit le contexte OnboardingContext : react à replay() sans re-monter le composant.
function OnboardingGate() {
  const { showOnboarding, isChecking, dismiss } = useOnboarding()

  // Attendre la fin de la vérification Supabase pour éviter le flash onboarding
  if (isChecking) return <GlobalSpinner />
  if (showOnboarding) return <OnboardingFlow onComplete={dismiss} />
  return <Layout />
}

// ── Route guards ─────────────────────────────────────────────────────────────
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <GlobalSpinner />
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/app'

  if (loading) return <GlobalSpinner />
  if (user) return <Navigate to={from} replace />
  return <>{children}</>
}

// Landing route — sur natif iOS/Android : redirige vers /auth ou /app
//                 sur web : affiche toujours la LandingPage
function LandingRoute() {
  const { user, loading } = useAuth()
  const { introSeen, markIntroSeen } = useIntroSeen()

  if (!Capacitor.isNativePlatform()) return <LandingPage />
  if (loading) return <GlobalSpinner />
  // User existant (déjà connecté après mise à jour) → skip intro silencieusement
  if (user && !introSeen) markIntroSeen()
  if (user) return <Navigate to="/app" replace />
  if (!introSeen) return <IntroSlider onDone={markIntroSeen} />
  return <Navigate to="/auth" replace />
}

// ── Push notification tap handler ─────────────────────────────────────────────
// Écoute le tap sur une notification push et navigue vers la route indiquée
// dans data.route. Inactif sur web.

function usePushNotificationTap() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cleanup: (() => void) | undefined

    const setup = async () => {
      try {
        const handle = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          async (action: any) => {
            const data = action.notification?.data

            // Milestone rating: prompt notation natif après un court délai
            if (data?.type === 'milestone_rating') {
              try {
                await new Promise(r => setTimeout(r, 1500))
                const { InAppReview } = await import('@capacitor-community/in-app-review')
                await InAppReview.requestReview()
              } catch (e) {
                console.log('[milestone] InAppReview error:', e)
              }
              return
            }

            // Group invitation: navigate to groups page where user can see/accept
            if (data?.type === 'group_invitation') {
              navigate('/app/groups')
              return
            }

            const route = data?.route
            if (route && typeof route === 'string') {
              navigate(route)
            } else {
              navigate('/app/notifications')
            }
          },
        )

        cleanup = () => handle.remove()
      } catch {
        // PushNotifications plugin not available
      }
    }

    setup()
    return () => cleanup?.()
  }, [navigate])
}

// ── Deep link handler ─────────────────────────────────────────────────────────
// Écoute les URL selah:// générées par les widgets iOS et redirige vers
// l'écran correspondant. Inactif sur web (Capacitor.isNativePlatform() = false).

function useDeepLinks() {
  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const listenerPromise = CapApp.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url)

        // ── Universal Links HTTPS (selah-app.fr) ────────────────────────────
        if (url.protocol === 'https:' && url.host === 'selah-app.fr') {
          const pathname = url.pathname
          if (pathname.startsWith('/g/')) {
            const code = pathname.slice(3)
            if (code) { navigate(`/g/${code}`); return }
          }
          if (pathname.startsWith('/invite/')) {
            const inviteCode = pathname.split('/invite/')[1]
            if (inviteCode) {
              localStorage.setItem('selah_invite_code', inviteCode)
              navigate(user ? '/app' : '/auth', { replace: true })
            }
            return
          }
          return
        }

        // URL format : selah://new-prayer, selah://home, etc.
        const path = url.host   // host = segment après "selah://"

        switch (path) {
          case 'auth': {
            // Retour OAuth Google sur iOS — url.host = 'auth' (pas 'auth/callback')
            // Supabase ne voit pas window.location sur Capacitor natif → on extrait
            // le fragment manuellement et on établit la session via setSession()
            const fragment = url.hash.substring(1)
            if (fragment) {
              const params       = new URLSearchParams(fragment)
              const accessToken  = params.get('access_token')
              const refreshToken = params.get('refresh_token') ?? ''
              if (accessToken) {
                supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
                  .then(() => navigate('/app', { replace: true }))
                  .catch(() => navigate('/auth', { replace: true }))
              } else {
                navigate('/auth', { replace: true })
              }
            } else {
              navigate(user ? '/app' : '/auth', { replace: true })
            }
            break
          }
          case 'new-prayer':   navigate('/app/prayer/new');  break
          case 'new-goal':     navigate('/app/goals/new');   break
          case 'new-note':     navigate('/app/journal');     break
          case 'new-verse':    navigate('/app/library/new'); break
          case 'home':         navigate('/app');             break
          case 'verse-of-day': navigate('/app/verse/1');     break
          case 'invite': {
            // selah://invite?code=ABCD1234 → stocker le code et rediriger
            const inviteCode = url.searchParams.get('code') ?? url.pathname.replace(/^\/+/, '')
            if (inviteCode) {
              localStorage.setItem('selah_invite_code', inviteCode)
              navigate(user ? '/app' : '/auth', { replace: true })
            }
            break
          }
          default:
            if (!import.meta.env.PROD)
              console.warn('[DeepLink] chemin inconnu :', path, '(URL :', event.url, ')')
        }
      } catch (err) {
        if (!import.meta.env.PROD)
          console.warn('[DeepLink] URL invalide :', event.url, err)
      }
    })

    // Nettoyage : supprime l'écouteur quand le composant est démonté
    return () => { listenerPromise.then(handle => handle.remove()) }
  }, [navigate, user])
}

// ── App routes ────────────────────────────────────────────────────────────────
function AppRoutes() {
  useDeepLinks()
  usePushNotificationTap()
  usePushTokenRegistration()
  const { user } = useAuth()
  const { showChangelog, dismissChangelog } = useChangelog()

  // Nettoyage proactif des notifications locales orphelines au démarrage
  // (prières supprimées dont les notifs locales persistent sur l'appareil)
  // + nettoyage des brouillons auto-save de plus de 7 jours
  useEffect(() => {
    scheduleRecurringPrayerNotifications()
    ensureDailyNotifAtStartup()
    ensureCheckInNotifAtStartup()
    cleanOldDrafts()
  }, [])

  return (
    <Suspense fallback={<GlobalSpinner />}>
      {user && showChangelog && (
        <ChangelogModal changelog={changelog} onDismiss={dismissChangelog} />
      )}
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingRoute />} />
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route path="/privacy"         element={<PrivacyPolicyPage />} />
        <Route path="/terms"           element={<TermsPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />
        <Route path="/download"        element={<DownloadPage />} />
        <Route path="/invite/auto"   element={<InviteAutoPage />} />
        <Route path="/dev/story-test" element={<StoryTestPage />} />
        <Route path="/invite/:code"   element={<InvitePage />} />
        <Route path="/g/:code"        element={<GroupInvitePage />} />

        {/* Protected — toutes les routes app sous /app */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <OnboardingProvider>
                <OnboardingGate />
              </OnboardingProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route path="journal" element={<Journal />} />
          <Route path="journal/:id" element={<DevotionalDetailPage />} />
          <Route path="notes/:id" element={<SermonNoteDetailPage />} />
          <Route path="prayer" element={<PrayerPage />} />
          <Route path="prayer/new" element={<NewPrayerPage />} />
          <Route path="prayer/edit/:id" element={<EditPrayerPage />} />
          <Route path="prayer/:id" element={<PrayerDetailPage />} />
          <Route path="goals" element={<GoalsPage />} />
          <Route path="goals/new" element={<NewGoalPage />} />
          <Route path="goals/edit/:id" element={<EditGoalPage />} />
          <Route path="goals/:id" element={<GoalDetailPage />} />
          <Route path="checkin" element={<CheckinPage />} />
          <Route path="library" element={<VerseLibraryPage />} />
          <Route path="library/new" element={<NewVersePage />} />
          <Route path="library/edit/:id" element={<EditVersePage />} />
          <Route path="library/search" element={<VerseSearchPage />} />
          <Route path="library/themes" element={<VerseThemesPage />} />
          {/* /app/release-notes → /app/notes pour compatibilité QuickAccessCard */}
          <Route path="release-notes" element={<Navigate to="/app/notes" replace />} />
          <Route path="notes" element={<NotesPage />} />
          <Route path="verse/:day?" element={<DailyVersePage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="friends" element={<FriendsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="notification-settings" element={<NotificationSettingsPage />} />
          <Route path="guide" element={<GuidePage />} />
          <Route path="groups"        element={<GroupPage />} />
          <Route path="groups/create" element={<CreateGroupPage />} />
          <Route path="groups/join"      element={<JoinGroupPage />} />
          <Route path="groups/answered" element={<GroupAnsweredPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const initApp = async () => {
      try {
        // Capgo notifyAppReady — avec timeout pour ne jamais bloquer le démarrage.
        // autoUpdate est désactivé pour la release store 1.17.0, mais notifyAppReady
        // reste nécessaire pour éviter un rollback si Capgo est réactivé plus tard.
        await Promise.race([
          CapacitorUpdater.notifyAppReady(),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ])
      } catch {
        // Capgo init skipped or timed out — non-bloquant
      } finally {
        try {
          await SplashScreen.hide({ fadeOutDuration: 300 })
        } catch {
          // SplashScreen déjà caché ou indisponible
        }
      }
    }

    initApp()
  }, [])

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
